import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { applyMeta, pageMeta } from '../seo.js';
import { EDITORIAL_ROUTES } from '../content/index.js';
import { pageData, loadData } from '../api.js';
import { resolutionBreakdown, confidenceSummary } from '../content/stats.js';

const META = EDITORIAL_ROUTES.find(r => r.path === '/methodology');

export default function Methodology() {
  const boot = pageData('methodology');
  const [stats, setStats] = useState(boot?.stats ?? null);

  useEffect(() => applyMeta(pageMeta({
    path: '/methodology',
    title: META.title,
    description: META.description,
  })), []);

  // Only runs on client-side navigation; the prerendered entry already has them.
  useEffect(() => {
    if (stats) return;
    let cancelled = false;
    loadData()
      .then(data => {
        if (cancelled) return;
        setStats({ breakdown: resolutionBreakdown(data), summary: confidenceSummary(data) });
      })
      .catch(err => console.error('Failed to load methodology stats:', err));
    return () => { cancelled = true; };
  }, [stats]);

  const summary = stats?.summary;
  const breakdown = stats?.breakdown;

  return (
    <article className="page-prose">
      <h1 className="page-heading">How TalkMuch measures a conversation</h1>
      <p className="page-subheading">
        What the percentage actually means, how we work out who the host is, and where the
        measurement is weakest.
      </p>

      <p>
        Every number on this site comes out of the same pipeline: fetch the episode audio,
        transcribe it, separate the voices, decide which voice is the host, remove the
        advertising, then measure what is left. Only the transcription step costs money. The
        interesting part, and the part that is genuinely ours, is deciding which speaker is
        the host, because that is the one thing the transcription service cannot tell us.
      </p>

      <h2>The problem nobody warns you about</h2>
      <p>
        Speaker diarization gives you speakers labelled A, B and C. It does not give you
        names. It has no idea which of them is the host, and for a site whose entire purpose
        is "how much did the host talk", getting that backwards inverts every number on the
        page.
      </p>
      <p>
        The obvious approach is to use the transcription service's own speaker
        identification, which tries to attach real names. We tried it and stopped using it.
        On interview shows it kept anchoring the host's name to whoever spoke over the intro
        jingle, and it was wrong close to half the time. The failure is structural rather
        than random: on plenty of episodes the guest's voice gets clustered together with the
        cold open, so the confident, named answer it hands back is confidently inverted.
      </p>

      <h2>Anchoring on the ad read</h2>
      <p>
        The idea that fixed it is simple. On a host-read show, the host reads the sponsor
        copy. Not the guest, not a narrator: the host. So instead of asking who sounds like
        the host, we ask <strong>who is holding the microphone during the adverts</strong>,
        and that speaker is the host.
      </p>
      <p>
        This works where the positional heuristics fail because it is grounded in what is
        being said rather than when it is said. The intro-jingle failure mode has no effect
        on it at all: whichever cluster the cold open landed in, the sponsor read still
        belongs to the host.
      </p>
      <p>The anchor refuses to answer unless it is confident:</p>
      <ul>
        <li>
          The episode must contain at least <strong>20 seconds</strong> of identified ad copy.
          Below that we are reading noise.
        </li>
        <li>
          One speaker must hold at least <strong>65%</strong> of that ad time. Two people
          trading sponsor lines is not evidence.
        </li>
        <li>
          The show must be configured as host-read. Some podcasts run programmatically
          inserted ads voiced by an actor who never appears in the episode, and on those the
          anchor is meaningless, so it is switched off entirely.
        </li>
      </ul>
      <p>
        When none of those hold, it returns nothing and the next method gets a turn. A
        detector that declines to guess is worth more than one that is confidently wrong half
        the time, which is exactly the lesson the named-speaker experiment taught us.
      </p>

      <h2>How we validated it before trusting it</h2>
      <p>
        A heuristic that sounds right is not evidence. Before enabling the ad anchor on any
        show, we replayed it over every episode of that show we had already corrected by
        hand, and required that on <em>every single one</em> it either agreed with the human
        answer or stayed silent. One contradiction disqualified the whole podcast.
      </p>
      <p>
        Because transcripts are cached, that replay cost nothing: we could re-resolve entire
        back catalogues as many times as we wanted without paying to transcribe anything
        again. That is the reason the validation could be exhaustive rather than a spot
        check.
      </p>
      <p>
        Shows that failed, or that were too ambiguous to call, simply did not get the anchor.
        Today it is enabled on 14 of the 27 podcasts we track, and it is the single largest
        source of host identifications on the site.
      </p>

      <h2>The full ladder</h2>
      <p>
        The anchor is one rung. Each method runs in turn and the first one that produces an
        answer wins, so the strongest available evidence decides. In order:
      </p>
      <ol>
        <li>
          <strong>One speaker only.</strong> A solo episode. Nothing to disambiguate.
        </li>
        <li>
          <strong>Who reads the ads.</strong> The anchor described above.
        </li>
        <li>
          <strong>A guest opener.</strong> Somebody in the first few exchanges says "thanks
          for having me", so they are the guest and the other speaker is the host. A handful
          of narrative shows quote that phrase inside a teaser, so they switch this off.
        </li>
        <li>
          <strong>The host introduces themselves.</strong> Matched against phrases configured
          per show, because every podcast says it differently.
        </li>
        <li>
          <strong>Who never says the host's name.</strong> Guests say the host's name;
          hosts rarely say their own. Inverted, this identifies the host by absence.
        </li>
        <li>
          <strong>Who asks the most questions.</strong> The last resort, and treated as such.
        </li>
      </ol>

      {breakdown && summary && (
        <>
          <p>
            Across the {summary.episodes.toLocaleString()} episodes currently published from{' '}
            {summary.podcasts} shows, this is how the host was actually identified:
          </p>
          <div className="table-scroll">
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th scope="col">How the host was identified</th>
                  <th scope="col" style={{ textAlign: 'right' }}>Episodes</th>
                  <th scope="col" style={{ textAlign: 'right' }}>Share</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.rows.map(row => (
                  <tr key={row.method}>
                    <td>{row.label}</td>
                    <td style={{ textAlign: 'right' }}>{row.count.toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }}>{row.pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            Every identification carries a confidence score. The median across the catalogue
            is {summary.medianConfidence}, and {summary.highConfidencePct}% of episodes were
            resolved at 0.9 or above. The {summary.lowConfidenceCount} episodes below 0.75
            are flagged automatically and reviewed by hand.
          </p>
        </>
      )}

      <h2>What the percentage actually measures</h2>
      <p>
        The host share is a percentage of <strong>conversation</strong>, not of episode
        length. Before anything is counted we remove:
      </p>
      <ul>
        <li>
          <strong>The pre-roll.</strong> The intro block before the episode proper begins,
          detected from per-show marker phrases. We only look for those markers in the first
          five minutes, because the same phrases turn up in the end credits and matching them
          there would truncate the entire episode.
        </li>
        <li>
          <strong>Every ad break.</strong> Not just the pre-roll: mid-roll, post-roll and
          dynamically inserted ads too, found by what they say rather than where they sit.
          Sponsor reads are usually pure host monologue, so leaving them in would inflate
          every host's share.
        </li>
      </ul>
      <p>
        So an 80/20 split means that of the actual talking, the host did 80% of it. Silence,
        music and advertising are not in the denominator.
      </p>
      <p>
        On panel shows the individual hosts are measured separately and then combined, with
        a guard against a mislabelled speaker inheriting an absent host's name, and a floor
        below which a stray fragment is folded back into the hosts rather than displayed as
        a phantom guest.
      </p>

      <h2>Where this is weakest</h2>
      <p>
        The honest section. These are known limitations, not hypothetical ones.
      </p>
      <ul>
        <li>
          <strong>Speaker separation makes mistakes, and they are not random.</strong> The
          common one is a host monologue or an ad read being attributed to the guest. It
          inflates guest talk time and, worse, inflates guest question counts.
        </li>
        <li>
          <strong>Question counts overstate real questions.</strong> We count question marks
          in the transcript, and a comedian telling a story produces a great many of them:
          "and I was like, what?" is punctuated as a question but nobody asked anything. On
          storytelling shows the rhetorical rate runs somewhere between 60% and 80%, so every
          batch gets reviewed by hand and the rhetorical ones are removed before the ratio is
          published.
        </li>
        <li>
          <strong>The question-count fallback is unreliable.</strong> When it is the only
          method left, the episode is by definition one where nothing else fired. On an
          evenly balanced conversation it is close to a coin flip, which is why it carries
          the lowest confidence on the site and why those episodes are flagged for review.
        </li>
        <li>
          <strong>Some shows defeat us.</strong> A host who says their own name constantly
          breaks the name heuristic. Narrative and documentary formats blur the host and
          guest roles until the question stops being meaningful. Where a show is structurally
          unmeasurable we would rather leave it out than publish a number we do not believe.
        </li>
        <li>
          <strong>Advertiser detection is good, not perfect.</strong> It finds sponsor blocks
          by phrasing and known brand names, which means a novel sponsor read in an unusual
          format can slip through and stay in the talk-time count.
        </li>
      </ul>

      <h2>Corrections</h2>
      <p>
        Every episode is checked automatically against a set of tripwires: low confidence, a
        host share high enough to look suspicious, a question ratio low enough to suggest an
        inversion, or resolution by the weakest fallback. Anything that trips one is logged
        and reviewed, and the log accumulates across runs, so a show that keeps failing the
        same way tells us to fix its configuration rather than patch episodes one at a time.
      </p>
      <p>
        Corrections made by hand are recorded as such, which is why they appear as their own
        row in the table above. If you think a figure is wrong,{' '}
        <Link to="/contact">tell us</Link> and we will re-check it against the stored
        transcript.
      </p>
    </article>
  );
}
